import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import StreamingMessage from './StreamingMessage';
import SettingsIcon from './SettingsIcon';
import SettingsModal from './SettingsModal';
import './AgenticAIApp.css';
import { useSelector } from 'react-redux';
import { processStreamingResponse } from './useSimpleStreaming';
import { useStreamingMessage } from './useStreamingMessage';
import { usePersonas } from './usePersonas';
import { downloadMessageAsMarkdown } from './downloadUtils';
import { discoverMCPTools } from './mcpUtils';
import { log } from './debug';
import { cleanupStaleTools } from './personaToolMigration';
import { DEFAULT_TOOLS, MANDATORY_TOOLS } from './defaultTools';
import { osndLocalStorage, storageConstant } from '../../helpers/localStorageUtils';

// API Configuration
const API_BASE_URL = 'http://localhost:8000';

function AgenticAIApp({ onClose, isMaximized, onToggleMaximize }) {
    const { enterpriseEcpdId: ecpdId = '', enterpriseId = '', name = '', solutionType = '' } = useSelector((state) => state.auth);
    const [input, setInput] = useState('');
    const [conversations, setConversations] = useState([]);
    const [isExecuting, setIsExecuting] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const isExecutingRef = useRef(false);
    const [sessionId, setSessionId] = useState(() => {
        const saved = localStorage.getItem('session_id');
        if (saved) {
            log('✓ Using existing session:', saved.substring(0, 8) + '...');
            return saved;
        }
        const newSessionId = crypto.randomUUID();
        localStorage.setItem('session_id', newSessionId);
        log('✓ Created new session:', newSessionId.substring(0, 8) + '...');
        return newSessionId;
    });

    // Production-grade streaming hook
    const { elementRef, textRef, isComplete, appendText, complete, reset } = useStreamingMessage();

    // Memoized download handler
    const handleDownloadMessage = useCallback((conv, index) => {
        downloadMessageAsMarkdown(conv, index, toast.success);
    }, []);

    const sessionIdRef = useRef(sessionId);
    useEffect(() => {
        sessionIdRef.current = sessionId;
    }, [sessionId]);
    const [mcpServers, setMcpServers] = useState(() => {
        const saved = localStorage.getItem('mcp_servers');
        return saved ? JSON.parse(saved) : [];
    });

    const [availableTools, setAvailableTools] = useState([]); // Tools from backend + discovered MCP tools
    const [llmConfig, setLlmConfig] = useState(() => {
        const saved = localStorage.getItem('llm_config');
        return saved
            ? JSON.parse(saved)
            : {
                  apiKey: ''
              };
    });

    // Personas state (using custom hook)
    const { personas, setPersonas, selectedPersona, setSelectedPersona } = usePersonas();

    const [isPersonaDropdownOpen, setIsPersonaDropdownOpen] = useState(false); // Custom dropdown state
    const personaDropdownRef = useRef(null); // Ref for click outside detection

    // Clear chat when enterprise changes (admin switching between enterprises)
    const prevEnterpriseIdRef = useRef(enterpriseId);
    useEffect(() => {
        if (prevEnterpriseIdRef.current === enterpriseId) return;
        prevEnterpriseIdRef.current = enterpriseId;
        if (!enterpriseId) return; // ignore logout/clear
        setConversations([]);
        const newSessionId = crypto.randomUUID();
        setSessionId(newSessionId);
        localStorage.setItem('session_id', newSessionId);
        log('✓ Enterprise changed, new session:', newSessionId.substring(0, 8) + '...');
    }, [enterpriseId]);

    // Track previous persona to detect actual changes
    const prevPersonaRef = useRef(selectedPersona);

    // Auto-clear chat when persona changes
    useEffect(() => {
        // Skip on initial mount
        if (prevPersonaRef.current === null) {
            prevPersonaRef.current = selectedPersona;
            return;
        }

        // Only clear if persona actually changed (not just conversations.length)
        if (prevPersonaRef.current !== selectedPersona && conversations.length > 0) {
            // Persona changed - clear chat and start fresh session
            setConversations([]);
            const newSessionId = crypto.randomUUID();
            setSessionId(newSessionId);
            localStorage.setItem('session_id', newSessionId);
            log('✓ Persona changed, new session:', newSessionId.substring(0, 8) + '...');
            prevPersonaRef.current = selectedPersona;
        }
    }, [selectedPersona]); // ✅ FIX: Remove conversations.length from dependencies

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (personaDropdownRef.current && !personaDropdownRef.current.contains(event.target)) {
                setIsPersonaDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // ✅ Debounced auto-scroll: Only scroll after updates settle (prevents scroll thrashing)
    useEffect(() => {
        const timer = setTimeout(() => scrollToBottom(), 200);
        return () => clearTimeout(timer);
    }, [conversations]);

    // Fetch available tools and discover MCP tools on page load
    // Initial discovery on mount - runs ONCE
    useEffect(() => {
        let isMounted = true;

        const initialDiscovery = async () => {
            // Only run if we have servers but they don't have tools yet (fresh load)
            const needsDiscovery = mcpServers.some((s) => !s.tools || s.tools.length === 0);
            if (!needsDiscovery || mcpServers.length === 0) {
                log('✓ Skipping discovery - servers already have tools or no servers configured');
                return;
            }

            setIsReconnecting(true);
            log('🔍 Initial discovery: Connecting to MCP servers...');

            try {
                const updatedServers = [];

                for (const server of mcpServers) {
                    if (!isMounted) break;

                    try {
                        const result = await discoverMCPTools(server);

                        if (result.error) {
                            updatedServers.push({
                                ...server,
                                status: 'error',
                                error: result.error,
                                toolCount: 0
                            });
                            log(`❌ ${server.name}: ${result.error}`);
                            toast.error(`Failed to connect to ${server.name}: ${result.error}`, {
                                toastId: `mcp-error-${server.name}`,
                                autoClose: 8000
                            });
                        } else {
                            const toolNames = result.tools.map((t) => t.name);
                            updatedServers.push({
                                ...server,
                                tools: toolNames,
                                toolCount: toolNames.length,
                                sessionId: result.sessionId,
                                requiresSession: result.requiresSession,
                                status: 'connected',
                                method: result.method
                            });
                            log(`✅ ${server.name}: ${toolNames.length} tools`);
                            toast.success(`Connected to ${server.name} (${toolNames.length} tools)`, {
                                toastId: `mcp-success-${server.name}`,
                                autoClose: 3000
                            });
                        }
                    } catch (error) {
                        updatedServers.push({
                            ...server,
                            status: 'error',
                            error: error.message,
                            toolCount: 0
                        });
                        console.error(`❌ ${server.name}: ${error.message}`);
                        toast.error(`Error connecting to ${server.name}: ${error.message}`, {
                            toastId: `mcp-catch-${server.name}`,
                            autoClose: 8000
                        });
                    }
                }

                if (!isMounted) return;

                if (updatedServers.length > 0) {
                    setMcpServers(updatedServers);
                    localStorage.setItem('mcp_servers', JSON.stringify(updatedServers));
                    const connectedCount = updatedServers.filter((s) => s.status === 'connected').length;
                    log(`✓ Initial discovery complete: ${connectedCount}/${updatedServers.length} servers connected`);
                }
            } catch (error) {
                console.error('✗ Initial discovery failed:', error);
            } finally {
                if (isMounted) {
                    setIsReconnecting(false);
                }
            }
        };

        initialDiscovery();

        return () => {
            isMounted = false;
        };
    }, []); // ✅ Only run once on mount

    // Build availableTools from current mcpServers state (no discovery here)
    // This updates whenever mcpServers changes, but doesn't trigger re-discovery
    useEffect(() => {
        const discoveredTools = [];

        mcpServers.forEach((server) => {
            if (server.status === 'connected' && server.tools && Array.isArray(server.tools)) {
                server.tools.forEach((toolName) => {
                    discoveredTools.push({
                        name: `mcp__${server.name}__${toolName}`,
                        description: toolName,
                        type: 'http_mcp',
                        server: server.name,
                        enabled_by_default: true
                    });
                });
            }
        });

        const allTools = [...DEFAULT_TOOLS, ...discoveredTools];
        setAvailableTools(allTools);

        if (discoveredTools.length > 0) {
            log(`✓ Available tools updated: ${DEFAULT_TOOLS.length} built-in + ${discoveredTools.length} MCP`);
        }
    }, [mcpServers]); // Only rebuild availableTools when mcpServers changes

    // Cleanup stale tools from personas when available tools change
    useEffect(() => {
        if (availableTools.length > 0 && personas.length > 0) {
            const { personas: cleanedPersonas, removedCount, staleTools } = cleanupStaleTools(personas, availableTools, MANDATORY_TOOLS);

            if (removedCount > 0) {
                log(`🧹 Auto-cleanup: Removed ${removedCount} stale tool references`);
                setPersonas(cleanedPersonas);

                // Show user-friendly notification
                toast.info(`Cleaned up ${removedCount} outdated tool reference${removedCount > 1 ? 's' : ''} from personas`, {
                    autoClose: 4000,
                    toastId: 'tool-cleanup'
                });
            }
        }
    }, [availableTools.length, personas.length]); // Run when tools or personas change

    const lastSubmissionRef = useRef(0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim() || isExecuting || isExecutingRef.current) return;

        // Prevent rapid duplicate submissions (within 500ms)
        const now = Date.now();
        if (now - lastSubmissionRef.current < 500) {
            log('⊝ Blocked duplicate submission (too fast)');
            return;
        }
        lastSubmissionRef.current = now;

        const userGoal = input.trim();
        setInput('');
        setIsExecuting(true);
        isExecutingRef.current = true; // Guard against concurrent calls

        // Prevent duplicate submissions
        const submissionId = now;

        // Add user message to conversation
        const userMessage = {
            id: submissionId,
            type: 'user',
            content: userGoal,
            timestamp: new Date()
        };

        // Create agent response container
        const agentResponse = {
            id: submissionId + 1,
            type: 'agent',
            submissionId: submissionId, // Track to prevent duplicates
            supervisorType: null,
            mode: null, // Router decision mode
            routerReasoning: null, // Why router chose this mode
            finalResponse: '',
            isStreaming: true, // ✅ Start streaming immediately - shows thinking indicator
            todos: [], // ✅ TODO tracking array
            errorRecovery: null, // Error recovery status
            cacheHits: 0, // Number of cache hits
            cacheSavings: 0, // Cost savings from cache ($)
            qualityMetrics: null, // Quality reflection metrics
            timestamp: new Date()
        };

        // ✅ FIX: Add both messages in a single state update to ensure agent message exists before streaming
        setConversations((prev) => [...prev, userMessage, agentResponse]);

        try {
            const recentConvos = [];
            const allowedTools = availableTools.filter((tool) => tool.enabled_by_default).map((tool) => tool.name);

            // Reset streaming state for new message
            reset();

            const linkedEnterprises = osndLocalStorage.getObject(storageConstant.LINKED_ENTERPRISES);
            const enterpriseIds =
                linkedEnterprises?.length > 0
                    ? linkedEnterprises
                          .filter((e) =>
                              linkedEnterprises.find((x) => x.enterpriseId === 'all_locations')?.selected
                                  ? e.enterpriseId !== 'all_locations'
                                  : e.selected
                          )
                          .map((e) => e.enterpriseId)
                          .join(',')
                    : '';
            const xsrfToken =
                document.cookie
                    .split('; ')
                    .find((row) => row.startsWith('XSRF-TOKEN='))
                    ?.split('=')[1] || '';

            const response = await fetch('/api/v1/mcp/execute', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-Id': sessionId,
                    'X-API-Key': llmConfig.apiKey || '',
                    'x-xsrf-token': xsrfToken,
                    ...(enterpriseIds && { 'enterprise-ids': enterpriseIds })
                },
                body: JSON.stringify({
                    goal: userGoal,
                    ecpd_id: ecpdId,
                    enterprise_id: enterpriseId,
                    solution_type: solutionType,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    session_id: sessionId,
                    allowed_tools: allowedTools,
                    conversation_history: recentConvos
                })
            });

            if (!response.ok) {
                let errorMessage = `Request failed (${response.status})`;
                try {
                    const errorBody = await response.json();
                    if (errorBody.error) {
                        errorMessage = errorBody.error;
                    } else if (errorBody.detail) {
                        const details = Array.isArray(errorBody.detail)
                            ? errorBody.detail.map((d) => `${d.loc?.slice(1).join('.')}: ${d.msg}`).join(', ')
                            : String(errorBody.detail);
                        errorMessage = `${errorMessage}: ${details}`;
                    }
                } catch (_) {
                    /* non-JSON error body */
                }
                setConversations((prev) => {
                    const newConv = [...prev];
                    const agentMsg = newConv[newConv.length - 1];
                    if (agentMsg && agentMsg.type === 'agent') {
                        agentMsg.finalResponse = `❌ **Error:** ${errorMessage}`;
                        agentMsg.isStreaming = false;
                    }
                    return newConv;
                });
                complete({ error: true });
                setIsExecuting(false);
                isExecutingRef.current = false;
                if (inputRef?.current) inputRef.current.focus();
                return;
            }

            // Process streaming response with direct DOM updates
            await processStreamingResponse(
                response,
                submissionId,
                setConversations,
                { appendText, complete },
                {
                    setSessionId,
                    setIsExecuting,
                    refs: { isExecutingRef, inputRef }
                }
            );
        } catch (error) {
            console.error('Error:', error);

            // Update last message with error
            setConversations((prev) => {
                const newConv = [...prev];
                const agentMsg = newConv[newConv.length - 1];
                if (agentMsg && agentMsg.type === 'agent') {
                    agentMsg.finalResponse = `❌ Network error: ${error.message}. Please check your connection and try again.`;
                }
                return newConv;
            });

            setIsExecuting(false);
            isExecutingRef.current = false;
        }
    };

    const handleClearChat = () => {
        // Clear conversation history in UI
        setConversations([]);
        setShowClearConfirm(false);

        // Generate new session ID to break Claude SDK resume chain
        const newSessionId = crypto.randomUUID();
        setSessionId(newSessionId);
        localStorage.setItem('session_id', newSessionId);

        log('✓ Chat cleared, new session:', newSessionId.substring(0, 8) + '...');
    };

    return (
        <div className="app">
            {/* Toast Notifications Container */}
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                pauseOnHover
                draggable
                theme="light"
                style={{ marginTop: '70px' }}
            />

            <div className="header">
                <div className="header-content">
                    <div className="header-actions">
                        {personas.length > 0 && (
                            <div className="persona-selector-header" ref={personaDropdownRef}>
                                <span className="persona-icon">🤖</span>
                                <div
                                    className="custom-select-trigger"
                                    onClick={() => setIsPersonaDropdownOpen(!isPersonaDropdownOpen)}
                                    title="Select a persona to chat with">
                                    <span className="custom-select-value">
                                        {!selectedPersona ? '-- Select a Persona --' : personas.find((p) => p.name === selectedPersona)?.name}
                                    </span>
                                    <span className={`custom-select-arrow ${isPersonaDropdownOpen ? 'open' : ''}`}>▼</span>
                                </div>

                                {isPersonaDropdownOpen && (
                                    <div className="custom-select-dropdown">
                                        {personas.map((persona) => (
                                            <div
                                                key={persona.id}
                                                className={`custom-select-option ${selectedPersona === persona.name ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setSelectedPersona(persona.name);
                                                    setIsPersonaDropdownOpen(false);
                                                }}>
                                                <span className="persona-name">{persona.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {conversations.length > 0 && (
                            <button className="clear-chat-btn" onClick={() => setShowClearConfirm(true)} title="Clear chat history">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                                Clear Chat
                            </button>
                        )}
                        {onToggleMaximize && (
                            <button className="drawer-maximize-btn" onClick={onToggleMaximize} title={isMaximized ? 'Restore' : 'Maximize'}>
                                {isMaximized ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                                    </svg>
                                )}
                            </button>
                        )}
                        {onClose && (
                            <button className="drawer-close-btn" onClick={onClose} title="Close">
                                ✕
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {isReconnecting && (
                <div className="app-loader-overlay">
                    <div className="app-loader-content">
                        <div className="app-loader-spinner"></div>
                        <div className="app-loader-text">Reconnecting MCP servers...</div>
                    </div>
                </div>
            )}

            {showClearConfirm && (
                <div className="modal-overlay" onClick={() => setShowClearConfirm(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Clear Session?</h3>
                        <p>
                            This will clear all conversation history, charts, and queries. Your session will start fresh. MCP server connections will
                            be reconnected.
                        </p>
                        <div className="modal-actions">
                            <button className="modal-btn cancel" onClick={() => setShowClearConfirm(false)}>
                                Cancel
                            </button>
                            <button className="modal-btn confirm" onClick={handleClearChat}>
                                Clear Session
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="chat-container">
                <div className="messages-container">
                    {conversations.length === 0 && (
                        <div className="welcome-message">
                            <h2>Welcome to Agentic AI {name ? `, ${name.split(' ')[0]}` : ''}</h2>
                        </div>
                    )}

                    {conversations.map((conv) => (
                        <div key={conv.id} className={`message ${conv.type}`}>
                            {conv.type === 'user' ? (
                                <div className="user-message">
                                    <div className="message-header">
                                        <span className="sender">You</span>
                                        <span className="timestamp">{conv.timestamp.toLocaleTimeString()}</span>
                                    </div>
                                    <div className="message-content">{conv.content}</div>
                                </div>
                            ) : (
                                <div className="agent-message">
                                    <div className="message-header">
                                        <span className="sender">Agent ⚡</span>
                                        <span className="timestamp">{conv.timestamp.toLocaleTimeString()}</span>
                                    </div>

                                    {/* Floating Download Button */}
                                    {conv.finalResponse && (
                                        <button
                                            className="floating-download-btn"
                                            onClick={() => handleDownloadMessage(conv, conversations.indexOf(conv))}
                                            title="Download as Markdown"
                                            aria-label="Download message">
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                <polyline points="7 10 12 15 17 10"></polyline>
                                                <line x1="12" y1="15" x2="12" y2="3"></line>
                                            </svg>
                                        </button>
                                    )}

                                    {/* Production-grade streaming message */}
                                    <StreamingMessage
                                        message={conv}
                                        elementRef={conv.id === conversations[conversations.length - 1]?.id ? elementRef : null}
                                    />

                                    {/* ✅ NEW: Error Recovery Indicator */}
                                    {conv.errorRecovery && (
                                        <div className="error-recovery-badge">
                                            <span className="icon">🔄</span>
                                            <span>
                                                {conv.errorRecovery.strategy === 'retry' && `Retrying (attempt ${conv.errorRecovery.attempt})...`}
                                                {conv.errorRecovery.strategy === 'escalate' && `Escalating to complex mode...`}
                                            </span>
                                        </div>
                                    )}

                                    {/* ✅ NEW: Cache Performance Indicator */}
                                    {conv.cacheHits > 0 && (
                                        <div className="cache-badge">
                                            <span className="icon">⚡</span>
                                            <span className="cache-text">
                                                {conv.cacheHits} cached result{conv.cacheHits > 1 ? 's' : ''}
                                            </span>
                                            {conv.cacheSavings > 0 && <span className="cache-savings">Saved ${conv.cacheSavings.toFixed(3)}</span>}
                                        </div>
                                    )}

                                    {/* ✅ NEW: Quality Metrics */}
                                    {conv.qualityMetrics && (
                                        <div className="quality-metrics">
                                            <div className="quality-header">
                                                <span>🔍 Quality: {conv.qualityMetrics.score}/10</span>
                                                {conv.qualityMetrics.pattern && (
                                                    <span className="quality-pattern">Pattern: {conv.qualityMetrics.pattern}</span>
                                                )}
                                            </div>
                                            {conv.qualityMetrics.whatWorked && (
                                                <div className="quality-detail success">✓ {conv.qualityMetrics.whatWorked}</div>
                                            )}
                                            {conv.qualityMetrics.whatFailed && (
                                                <div className="quality-detail failure">✗ {conv.qualityMetrics.whatFailed}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSubmit} className="input-form">
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={isExecuting ? '✳ Thinking...' : 'Ask me anything...'}
                        disabled={isExecuting}
                        className="chat-input"
                        autoFocus
                    />
                    <button type="submit" disabled={isExecuting || !input.trim()} className="send-btn">
                        <span>→</span>
                    </button>
                </form>
            </div>

            {/* Floating Settings Button - Bottom Right */}
            <button className="floating-settings-btn" onClick={() => setShowSettings(true)}>
                <SettingsIcon size={24} />
            </button>

            {/* Settings Modal */}
            {showSettings && (
                <SettingsModal
                    mcpServers={mcpServers}
                    sessionId={sessionId}
                    llmConfig={llmConfig}
                    setLlmConfig={setLlmConfig}
                    personas={personas}
                    onPersonasChange={setPersonas}
                    onClose={() => setShowSettings(false)}
                    onSave={(servers) => {
                        setMcpServers(servers);
                        localStorage.setItem('mcp_servers', JSON.stringify(servers));
                    }}
                    onToolsDiscovered={(discoveredTools) => {
                        // Update availableTools immediately when Settings discovers tools
                        const allTools = [...DEFAULT_TOOLS, ...discoveredTools];
                        setAvailableTools(allTools);
                        log(`✓ Tools updated from Settings: ${discoveredTools.length} MCP tools`);
                    }}
                    availableTools={availableTools}
                />
            )}
        </div>
    );
}

export default AgenticAIApp;
